import { FeedbackType } from '@/src/domain/usecases/SubmitFeedbackUseCase';
import { useTranslation } from '@/src/i18n';
import { Ionicons } from '@expo/vector-icons';
import { ScrollView, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from './FeedbackScreen.styles';

const MAX_CHARS = 1000;

const FEEDBACK_TYPES: { type: FeedbackType; icon: keyof typeof Ionicons.glyphMap }[] = [
  { type: 'bug', icon: 'bug-outline' },
  { type: 'feature', icon: 'bulb-outline' },
  { type: 'general', icon: 'chatbubble-outline' },
];

export interface FeedbackScreenProps {
  selectedType: FeedbackType;
  message: string;
  submitting: boolean;
  onTypeChange: (type: FeedbackType) => void;
  onMessageChange: (text: string) => void;
  onSubmit: () => void;
  colors: {
    background: string;
    surface: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    card: string;
  };
}

export const FeedbackScreen = ({
  selectedType,
  message,
  submitting,
  onTypeChange,
  onMessageChange,
  onSubmit,
  colors,
}: FeedbackScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
    >
      <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
        {t('feedback.subtitle')}
      </Text>

      <View>
        <Text style={[styles.label, { color: colors.textSecondary }]}>
          {t('feedback.typeLabel').toUpperCase()}
        </Text>
        <View style={styles.typeRow}>
          {FEEDBACK_TYPES.map(({ type, icon }) => {
            const isSelected = selectedType === type;
            return (
              <TouchableOpacity
                key={type}
                style={[
                  styles.typeChip,
                  {
                    borderColor: isSelected ? colors.primary : colors.border,
                    backgroundColor: isSelected ? colors.primary + '18' : colors.card,
                  },
                ]}
                onPress={() => onTypeChange(type)}
              >
                <Ionicons
                  name={icon}
                  size={16}
                  color={isSelected ? colors.primary : colors.textSecondary}
                />
                <Text
                  style={[
                    styles.typeChipText,
                    { color: isSelected ? colors.primary : colors.textSecondary },
                  ]}
                >
                  {t(`feedback.type.${type}`)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <View>
        <Text style={[styles.label, { color: colors.textSecondary }]}>
          {t('feedback.messageLabel').toUpperCase()}
        </Text>
        <TextInput
          style={[styles.textArea, { borderColor: colors.border, color: colors.text, backgroundColor: colors.card }]}
          placeholder={t('feedback.messagePlaceholder')}
          placeholderTextColor={colors.textTertiary}
          value={message}
          onChangeText={(text) => onMessageChange(text.slice(0, MAX_CHARS))}
          multiline
          autoCorrect={false}
        />
        <Text style={[styles.charCount, { color: colors.textTertiary }]}>
          {message.length}/{MAX_CHARS}
        </Text>
      </View>

      <TouchableOpacity
        style={[
          styles.submitButton,
          { backgroundColor: submitting || !message.trim() ? colors.border : colors.primary },
        ]}
        onPress={onSubmit}
        disabled={submitting || !message.trim()}
      >
        <Text style={[styles.submitButtonText, { color: submitting || !message.trim() ? colors.textSecondary : colors.primaryText }]}>
          {submitting ? t('feedback.submitting') : t('feedback.submit')}
        </Text>
      </TouchableOpacity>
    </ScrollView>
  );
};
